import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { teknikTokens } from '../styles/teknik-tokens.js';

@customElement('app-search')
export class AppSearch extends LitElement {
  @state() query = '';
  @state() results: any = { users: [], pages: [], store: [], commands: [] };
  @state() loading = false;
  @state() open = false;
  @state() activeIdx = -1;
  private debounceTimer: any = null;

  get flatResults() {
    const items: any[] = [];
    for (const u of this.results.users) items.push({ ...u, _type: 'user' });
    for (const p of this.results.pages) items.push({ ...p, _type: 'page' });
    for (const s of this.results.store) items.push({ ...s, _type: 'store' });
    for (const c of this.results.commands) items.push({ ...c, _type: 'command' });
    return items;
  }

  onInput(e: any) {
    this.query = e.target.value;
    this.activeIdx = -1;
    clearTimeout(this.debounceTimer);
    if (!this.query.trim()) {
      this.results = { users: [], pages: [], store: [], commands: [] };
      this.open = false;
      return;
    }
    this.open = true;
    this.debounceTimer = setTimeout(() => this.doSearch(), 280);
  }

  async doSearch() {
    if (this.query.trim().length < 2) return;
    this.loading = true;
    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(this.query)}`);
      this.results = await res.json();
    } catch {}
    this.loading = false;
  }

  onKeyDown(e: KeyboardEvent) {
    const flat = this.flatResults;
    if (!this.open || !flat.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); this.activeIdx = Math.min(this.activeIdx + 1, flat.length - 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this.activeIdx = Math.max(this.activeIdx - 1, -1); }
    else if (e.key === 'Enter' && this.activeIdx >= 0) { e.preventDefault(); this.navigate(flat[this.activeIdx]); }
    else if (e.key === 'Escape') { this.open = false; this.activeIdx = -1; }
  }

  navigate(item: any) {
    const url = item._type === 'user' ? item.url : item.url;
    this.query = '';
    this.results = { users: [], pages: [], store: [], commands: [] };
    this.open = false;
    this.activeIdx = -1;
    window.location.href = url;
  }

  get hasResults() {
    return this.flatResults.length > 0;
  }

  static styles = [
    teknikTokens,
    css`
    :host { display: block; position: relative; font-family: var(--font-family); }

    .search-wrap { position: relative; }

    .search-input {
      width: 100%; background: var(--tk-panel-2); border: 1px solid var(--tk-border);
      border-radius: var(--tk-r-control); padding: .42rem .85rem .42rem 2.1rem;
      font-family: var(--font-family);
      font-size: .85rem; color: var(--tk-text); outline: none;
      transition: border-color .15s, background .15s; box-sizing: border-box;
    }
    .search-input::placeholder { color: var(--tk-dim); }
    .search-input:focus {
      border-color: var(--tk-accent-line); background: var(--tk-panel-2);
    }
    /* Inlined rather than masked off the bootstrap-icons CDN — the header is on
       every page, so that was a network round trip for a 13px glyph. */
    .search-icon {
      position: absolute; left: .72rem; top: 50%; transform: translateY(-50%);
      width: 13px; height: 13px;
      background: var(--tk-dim);
      -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='2.2' stroke-linecap='round'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='m20 20-3.8-3.8'/%3E%3C/svg%3E");
      mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='2.2' stroke-linecap='round'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='m20 20-3.8-3.8'/%3E%3C/svg%3E");
      -webkit-mask-size: contain; mask-size: contain;
      -webkit-mask-repeat: no-repeat; mask-repeat: no-repeat;
    }
    .spinner {
      position: absolute; right: .7rem; top: 50%; transform: translateY(-50%);
      width: 12px; height: 12px; border: 2px solid var(--tk-border);
      border-top-color: var(--tk-accent); border-radius: 50%;
      animation: spin .5s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg) translateY(-50%); } }

    .dropdown {
      position: absolute; top: calc(100% + 8px); left: 50%; transform: translateX(-50%);
      width: 380px; max-width: 95vw;
      background: var(--tk-panel); border: 1px solid var(--tk-border);
      border-radius: var(--tk-r-card); box-shadow: 0 16px 48px rgba(0,0,0,.55);
      z-index: 9999; overflow: hidden; padding: 6px 0;
    }

    .section-label {
      font-family: var(--font-mono);
      font-size: .625rem; font-weight: 500; color: var(--tk-dim);
      text-transform: uppercase; letter-spacing: .12em;
      padding: .6rem .85rem .25rem;
    }

    .result-item {
      display: flex; align-items: center; gap: 10px;
      padding: .48rem .85rem; cursor: pointer; transition: background .1s;
      border-radius: 0;
    }
    .result-item:hover, .result-item.active { background: var(--tk-panel-2); }

    .result-icon {
      width: 30px; height: 30px; border-radius: 8px;
      background: var(--tk-panel-2); display: flex;
      align-items: center; justify-content: center;
      flex-shrink: 0; font-size: 1rem; overflow: hidden;
    }
    .result-icon img {
      width: 100%; height: 100%; object-fit: cover; border-radius: 50%;
    }
    .result-icon.user-icon {
      background: var(--tk-accent-soft);
      font-size: .75rem; font-weight: 600; color: var(--tk-accent);
    }

    .result-text { flex: 1; min-width: 0; }
    .result-name { font-size: .84rem; font-weight: 600; color: var(--tk-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .result-desc { font-size: .72rem; color: var(--tk-dim); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

    /* The badge text already names the category, so the colour would only be
       redundant encoding — and three extra hues in a one-accent site. */
    .result-badge {
      font-family: var(--font-mono);
      font-size: .625rem; font-weight: 500; padding: .15rem .4rem;
      border-radius: 4px; flex-shrink: 0;
      background: var(--tk-panel-2); color: var(--tk-dim);
    }
    .no-results {
      padding: 1.1rem .85rem; text-align: center;
      font-size: .82rem; color: var(--tk-dim);
    }

    .divider { height: 1px; background: var(--tk-border); margin: 4px 0; }

    .kbd-hint {
      padding: .45rem .85rem; font-size: .65rem; color: var(--tk-dim);
      display: flex; gap: .5rem; align-items: center;
      border-top: 1px solid var(--tk-border);
    }
    kbd {
      background: var(--tk-panel-2); border: 1px solid var(--tk-border);
      border-radius: 4px; padding: .1rem .3rem; font-size: .65rem;
      font-family: var(--font-mono); color: var(--tk-muted);
    }
  `,
  ];

  private _renderSection(label: string, items: any[], type: string, globalOffset: number) {
    if (!items.length) return html``;
    return html`
      <div class="section-label">${label}</div>
      ${items.map((item, i) => {
        const globalI = globalOffset + i;
        const isActive = this.activeIdx === globalI;
        if (type === 'user') return html`
          <div class="result-item ${isActive ? 'active' : ''}" @click=${() => this.navigate(item)}>
            <div class="result-icon user-icon">
              ${item.avatar
                ? html`<img src="${item.avatar}" alt="" onerror="this.style.display='none'" />`
                : html`${item.username?.[0]?.toUpperCase() || '?'}`}
            </div>
            <div class="result-text">
              <div class="result-name">${item.username}</div>
              <div class="result-desc">Teknik user${item.discordLinked ? ' · Discord linked' : ''}</div>
            </div>
            <span class="result-badge badge-user">User</span>
          </div>`;
        if (type === 'store') return html`
          <div class="result-item ${isActive ? 'active' : ''}" @click=${() => this.navigate(item)}>
            <div class="result-icon">${item.icon}</div>
            <div class="result-text">
              <div class="result-name">${item.title}</div>
              <div class="result-desc">${item.desc}</div>
            </div>
            <span class="result-badge badge-store">Store</span>
          </div>`;
        if (type === 'command') return html`
          <div class="result-item ${isActive ? 'active' : ''}" @click=${() => this.navigate(item)}>
            <div class="result-icon" style="font-size:.8rem;font-family:monospace;color:#60a5fa">/</div>
            <div class="result-text">
              <div class="result-name">/${item.name}</div>
              <div class="result-desc">${item.desc}</div>
            </div>
            <span class="result-badge badge-command">Command</span>
          </div>`;
        return html`
          <div class="result-item ${isActive ? 'active' : ''}" @click=${() => this.navigate(item)}>
            <div class="result-icon">${item.icon || '📄'}</div>
            <div class="result-text">
              <div class="result-name">${item.title}</div>
              <div class="result-desc">${item.desc}</div>
            </div>
          </div>`;
      })}
    `;
  }

  render() {
    const show = this.open && this.query.trim().length >= 2;
    const { users = [], pages = [], store = [], commands = [] } = this.results;
    let offset = 0;
    const usersOff = offset; offset += users.length;
    const pagesOff = offset; offset += pages.length;
    const storeOff = offset; offset += store.length;
    const commandsOff = offset;

    return html`
      <div class="search-wrap">
        <span class="search-icon"></span>
        <input
          class="search-input"
          type="text"
          placeholder="Search users, store, pages…"
          autocomplete="off"
          .value=${this.query}
          @input=${this.onInput}
          @keydown=${this.onKeyDown}
          @focus=${() => { if (this.query.trim().length >= 2) this.open = true; }}
          @blur=${() => setTimeout(() => { this.open = false; }, 200)}
        />
        ${this.loading ? html`<div class="spinner"></div>` : ''}

        ${show ? html`
          <div class="dropdown">
            ${!this.hasResults && !this.loading
              ? html`<div class="no-results">No results for "<strong>${this.query}</strong>"</div>`
              : html`
                ${this._renderSection('Users', users, 'user', usersOff)}
                ${users.length && pages.length ? html`<div class="divider"></div>` : ''}
                ${this._renderSection('Pages', pages, 'page', pagesOff)}
                ${pages.length && store.length ? html`<div class="divider"></div>` : ''}
                ${this._renderSection('Store', store, 'store', storeOff)}
                ${store.length && commands.length ? html`<div class="divider"></div>` : ''}
                ${this._renderSection('Bot Commands', commands, 'command', commandsOff)}
              `}
            ${this.hasResults ? html`
              <div class="kbd-hint">
                <kbd>↑↓</kbd> navigate &nbsp; <kbd>↵</kbd> open &nbsp; <kbd>esc</kbd> close
              </div>
            ` : ''}
          </div>
        ` : ''}
      </div>
    `;
  }
}
